import type { Stop } from "../types";
import { API_ENDPOINTS } from "./apiEndpoints";
import { getActiveTpid, getFeatureTypeName } from "./timetablePeriods";
import { parseLineList } from "../utils/parseLineList";

const TRIM = (s?: string | null) => (s ?? "").trim();

function buildLocationsRequestXml(
  typeName: string,
  tpid: string,
  rootId: string
) {
  const props = [
    "hplnr",
    "lage",
    "alias",
    "hplnam",
    "linjelista",
    "geom",
    "tpid",
  ]
    .map((p) => `<wfs:PropertyName>${p}</wfs:PropertyName>`)
    .join("");

  return `
<GetFeature service="WFS" version="1.0.0" outputFormat="application/json"
  xmlns:wfs="http://www.opengis.net/wfs"
  xmlns:ogc="http://www.opengis.net/ogc"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-basic.xsd">
  <wfs:Query typeName="${typeName}">
    ${props}
    <ogc:Filter>
      <ogc:And>
        <ogc:PropertyIsEqualTo>
          <ogc:PropertyName>tpid</ogc:PropertyName>
          <ogc:Literal>${tpid}</ogc:Literal>
        </ogc:PropertyIsEqualTo>
        <ogc:PropertyIsEqualTo>
          <ogc:PropertyName>hplnr</ogc:PropertyName>
          <ogc:Literal>${rootId}</ogc:Literal>
        </ogc:PropertyIsEqualTo>
      </ogc:And>
    </ogc:Filter>
  </wfs:Query>
</GetFeature>`.trim();
}

export async function fetchLocationsForRoot(
  rootId: string,
  requestedTpid?: string
): Promise<Stop[]> {
  const tpidVal = requestedTpid ?? (await getActiveTpid());

  if (tpidVal == null) {
    throw new Error("Ingen aktiv tidtabellsperiod hittades");
  }

  const tpid = String(tpidVal);

  const typeName =
    (await getFeatureTypeName("StopPoint").catch(() => null)) ?? "ogt:hpll3";

  const xml = buildLocationsRequestXml(typeName, tpid, rootId);

  const res = await fetch(API_ENDPOINTS.GEO_FETCH, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    },
    body: `=${encodeURIComponent(xml)}`,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");

    console.error("Geo/fetch (Locations) fel:", res.status, text);

    throw new Error("Geo/fetch (Locations) fel: " + res.status);
  }

  const geojson = (await res.json()) as {
    features?: Array<{
      geometry?: { coordinates?: [number, number] };
      properties?: Record<string, unknown>;
    }>;
  };

  const feats = geojson?.features ?? [];

  return feats.map((f) => {
    const p = f.properties ?? {};
    const coords = f.geometry?.coordinates ?? [0, 0];
    const [lng, lat] = coords;

    const hplnr = String(p["hplnr"]);
    const alias = TRIM(String(p["alias"] ?? ""));
    const name = TRIM(String(p["hplnam"] ?? ""));

    const id = alias ? `${hplnr}-${alias}` : hplnr;

    return {
      id,
      name: alias ? `${name} (${alias})` : name,
      lat,
      lng,
      type: alias || undefined,
      servedLineIds: parseLineList(String(p["linjelista"] ?? "")) ?? [],
    } as Stop;
  });
}
